import {
  childrenToString,
  stripInlineMarkdown,
  warnOnce,
} from "../../internal/functions";
import { useTrackingId } from "../../hooks";
import { DataTrackingId, LayoutUtilProps, Size } from "../../types";
import { YearlessDate, YearlessDateMode } from "./types";
import {
  YearlessDateInputWithPicker,
  YearlessDateInputWithPickerRef,
} from "./internal/YearlessDateInputWithPicker";
import { useLayoutPropsUtil } from "../../internal/hooks";
import {
  useId,
  useRef,
  isValidElement,
  ReactElement,
  ReactNode,
  ComponentPropsWithoutRef,
} from "react";
import { FieldLabel, FieldLabelProps } from "../FieldLabel";
import { Helper, HelperProps } from "../../internal/components";
import { warnDeprecatedErrorUsage } from "../FieldMessage";
import styles from "../TextField/TextField.module.scss";
import cx from "classnames";

export type DateFieldYearlessProps = Omit<
  ComponentPropsWithoutRef<"input">,
  | "onChange"
  | "value"
  | "defaultValue"
  | "ref"
  | "size"
  | "type"
  | "onFocus"
  | "onBlur"
> &
  LayoutUtilProps & {
    /**
     * The controlled value of the date field
     */
    value?: YearlessDate | null;
    /**
     * The default value for uncontrolled usage
     */
    defaultValue?: YearlessDate | null;
    /**
     * The function to call when the date value changes
     */
    onChange?: DateFieldYearlessChangeHandler;
    /**
     * The date format mode (mm/dd or dd/mm)
     */
    mode?: YearlessDateMode;
    /**
     * Whether the field is required
     */
    required?: boolean;
    /**
     * The minimum allowed date
     */
    minDate?: YearlessDate | null;
    /**
     * The maximum allowed date
     */
    maxDate?: YearlessDate | null;
    /**
     * Configuration for unavailable dates
     */
    unavailable?: {
      /**
       * Array of dates that are not selectable
       */
      dates?: YearlessDate[];
    };
    /**
     * Whether the picker is disabled
     */
    disablePicker?: boolean;
    /**
     * Error state for the field. Pass `true` to indicate error styling without a message.
     * Pass a string, string[], or ReactElement (deprecated) for error messages.
     */
    error?: boolean | string | ReactElement | string[];
    // TODO(v4.0.0): make label required and type string
    /**
     * Label for the field.
     * Omitting `label` is deprecated — it will be required in v4.0.0. Use `hideLabel` to visually hide it.
     * Passing `ReactNode` is deprecated — use a plain string with inline markdown instead.
     */
    label?: FieldLabelProps["children"];
    /**
     * Props for the label component
     */
    labelProps?: FieldLabelProps;
    /**
     * Description text for the field
     */
    description?: HelperProps["description"];
    /**
     * @deprecated No longer used. Error messages always use `aria-live="assertive"`.
     */
    errorAriaLive?: HelperProps["errorAriaLive"];
    /**
     * Warning message(s) to display. Supports a single string or an array of strings.
     */
    warning?: string | string[];
    /**
     * Size of the input
     */
    size?: Extract<Size, "small" | "medium" | "large">;
    /**
     * Whether the field is disabled
     */
    disabled?: boolean;
    /**
     * Whether the field is in loading state
     */
    loading?: boolean;
    /**
     * Additional info content
     */
    moreInfo?: ReactNode;
    /**
     * Whether to disable the format hint
     */
    disableHint?: boolean;
    /**
     * When `true`, hides the visible label and moves it to `aria-label` on the input.
     * @default false
     */
    hideLabel?: boolean;
    /**
     * Function to call when the field is focused
     */
    onFocus?: (event: React.FocusEvent) => void;
    /**
     * Function to call when the field is blurred
     */
    onBlur?: (event: React.FocusEvent) => void;
  } & DataTrackingId;

/**
 * Represents a change event for the DateFieldYearless component
 */
export type DateFieldYearlessChange = {
  /**
   * The current date value
   */
  value: YearlessDate | null;
  /**
   * Whether the input is valid
   */
  isInputValid: boolean;
  /**
   * Whether the input is empty
   */
  isInputEmpty: boolean;
  /**
   * Whether the overall value is valid
   */
  isValid: boolean;
};

/**
 * Handler function for DateFieldYearless change events
 */
export type DateFieldYearlessChangeHandler = (
  change: DateFieldYearlessChange,
) => void;

/**
 * DateFieldYearless component for inputting dates without year information.
 *
 * Features:
 * - Supports both controlled and uncontrolled usage
 * - Supports different date formats (mm/dd or dd/mm)
 * - Supports layout utilities for positioning and spacing
 *
 * @example
 * <DateFieldYearless
 *   label="Birthday"
 *   mode="mm/dd"
 *   required
 *   onChange={(change) => console.log('Date changed:', change.value)}
 * />
 */
export const DateFieldYearless = (props: DateFieldYearlessProps) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    value,
    defaultValue,
    minDate,
    maxDate,
    unavailable,
    required,
    disablePicker = false,
    mode = "mm/dd",
    error,
    errorAriaLive: _errorAriaLive,
    label,
    moreInfo,
    size,
    disabled,
    loading,
    labelProps,
    warning,
    description,
    id: idProp,
    style,
    disableHint = false,
    hideLabel = false,
    onFocus,
    onBlur,
    className,
    onChange,
    ...restProps
  } = componentProps;

  const inputRef = useRef<YearlessDateInputWithPickerRef>(null);

  const { aiMark, ...labelPropsWithoutAiMark } = labelProps || {};

  const data = {
    label: childrenToString(props.label),
    labelProps: labelPropsWithoutAiMark,
    description: childrenToString(props.description),
  };

  const trackingId = useTrackingId({
    name: "DateFieldYearless",
    data,
    hasOverride: !!props["data-tracking-id"],
  });

  const labelUid = useId();
  const id = idProp ?? labelUid;
  const helperUid = useId();
  const moreInfoUid = useId();

  warnDeprecatedErrorUsage("DateFieldYearless", error);

  // TODO(v4.0.0): remove these warnings once label is required string
  if (label === undefined) {
    warnOnce(
      "DateFieldYearless: label will be required in v4.0.0. Use hideLabel to visually hide the label.",
    );
  } else if (typeof label !== "string") {
    warnOnce(
      "DateFieldYearless: Passing ReactNode to label is deprecated. Use a plain string with inline markdown instead.",
    );
  }

  const errorMessages =
    typeof error === "boolean" || error === undefined ? undefined : error;

  const styleCombined = { ...style, ...layoutStyles };

  const ariaDescribedBy = [helperUid, moreInfo && moreInfoUid]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={cx(styles["textfield"], className)}
      data-anv="datefield-yearless"
      style={styleCombined}
    >
      {/* TODO(v4.0.0): simplify to !hideLabel once label is required */}
      {label && !hideLabel && (
        <FieldLabel
          moreInfo={moreInfo}
          moreInfoId={moreInfo ? moreInfoUid : undefined}
          required={required}
          htmlFor={id}
          {...labelProps}
        >
          {label}
        </FieldLabel>
      )}
      <YearlessDateInputWithPicker
        {...restProps}
        ref={inputRef}
        mode={mode}
        required={required}
        minDate={minDate}
        maxDate={maxDate}
        unavailable={unavailable}
        disablePicker={disablePicker}
        size={size}
        error={!!error}
        disabled={disabled}
        loading={loading}
        id={id}
        data-tracking-id={trackingId}
        aria-describedby={ariaDescribedBy}
        aria-invalid={error ? !!error : undefined}
        aria-label={
          // TODO(v4.0.0): remove typeof check — label will always be string
          hideLabel && typeof label === "string"
            ? stripInlineMarkdown(label)
            : label
              ? undefined
              : "Select a date"
        }
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {errorMessages || warning || description || (!disableHint && mode) ? (
        <Helper
          id={helperUid}
          hint={disableHint ? undefined : `Format: ${mode}`}
          showCounter={false}
          error={
            typeof errorMessages === "string" || Array.isArray(errorMessages)
              ? errorMessages
              : undefined
          }
          warning={warning}
          errorMessage={
            isValidElement(errorMessages) ? errorMessages : undefined
          }
          description={description}
        />
      ) : null}
    </div>
  );
};

DateFieldYearless.displayName = "DateFieldYearless";
